import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button, ChipGroup, EmptyState, PageHeader, Spinner } from "@magarac/ui";
import { customerDisplayName, customerPageSchema } from "@magarac/shared/schemas/customer";
import { HelpIcon } from "../../../components/HelpIcon";
import { NewCustomerDialog } from "../../../components/NewCustomerDialog";
import { api } from "../../../lib/api";
import { formatMoney, formatPhone } from "../../../lib/format";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/customers/")({
  validateSearch: (s: Record<string, unknown>): { owes?: boolean } => ({
    owes: s.owes === true || s.owes === "true" || s.owes === "1" ? true : undefined,
  }),
  component: CustomersPage,
});

/** CRM-1 list, newest first; search lives in the search box. Screen key: customers.list */
function CustomersPage() {
  const { active } = useTenant();
  const [adding, setAdding] = useState(false);
  // CRM-29: archived customers are out of the way but one tap from being restored.
  const search = Route.useSearch();
  const [show, setShow] = useState<"exclude" | "only" | "owes">(search.owes ? "owes" : "exclude");
  const q = useInfiniteQuery({
    queryKey: ["customers", active?.id, "list", show],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get("/customers", customerPageSchema, {
        query: {
          limit: 50,
          cursor: pageParam,
          ...(show === "owes" ? { owes: "true" } : { archived: show }),
        },
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(active),
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Customers"
        help={<HelpIcon screen="customers.list" />}
        actions={
          <>
            <Link to="/import" className="hidden text-brand-600 underline md:inline">
              Import
            </Link>
            <Button onClick={() => setAdding(true)}>Add customer</Button>
          </>
        }
      />
      <Link
        to="/search"
        className="mb-4 block rounded-lg border border-line bg-surface p-3 text-muted md:hidden"
      >
        Search customers, addresses, and jobs
      </Link>
      <div className="mb-4">
        <ChipGroup
          label="Show"
          value={show}
          onChange={setShow}
          options={[
            { value: "exclude", label: "Active" },
            { value: "owes", label: "Owes money" },
            { value: "only", label: "Archived" },
          ]}
        />
      </div>
      {q.isLoading ? <Spinner /> : null}
      {q.data && items.length === 0 && show === "owes" ? (
        <EmptyState title="Nobody owes you money right now" />
      ) : null}
      {q.data && items.length === 0 && show === "only" ? (
        <EmptyState title="No archived customers" />
      ) : null}
      {q.data && items.length === 0 && show === "exclude" ? (
        <EmptyState
          title="No customers yet"
          body="Add them one at a time or bring them all in from a spreadsheet."
          action={
            <Link to="/import" className="text-brand-600 underline">
              Import customers
            </Link>
          }
        />
      ) : null}
      <ul className="divide-y divide-line rounded-card border border-line bg-surface empty:hidden">
        {items.map((c) => (
          <li key={c.id}>
            <Link
              to="/customers/$id"
              params={{ id: c.id }}
              className="flex items-center justify-between gap-3 p-4 hover:bg-brand-50"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{customerDisplayName(c)}</div>
                <div className="truncate text-sm text-muted">
                  {[c.primaryPhone ? formatPhone(c.primaryPhone) : null, c.email]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              {c.balanceCents > 0 ? (
                <span className="text-sm text-warn">Owes {formatMoney(c.balanceCents)}</span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
      {q.hasNextPage ? (
        <Button
          variant="ghost"
          className="mt-3"
          onClick={() => void q.fetchNextPage()}
          busy={q.isFetchingNextPage}
        >
          Show more
        </Button>
      ) : null}
      <NewCustomerDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}
